<template>
  <div class="flex flex-col section-y" :class="sectionBackgroundClass">
    <div class="max-w-(--breakpoint-xl) m-auto px-4">
      <h2
        v-if="content?.title"
        :class="[isLightTheme ? 'text-center' : 'text-stone-50']"
        class="grid-title section-title mb-10"
      >
        {{ content.title }}
      </h2>
      <p
        v-if="content?.subtitle"
        class="text-h5 font-bold text-stone-800"
        :class="[isLightTheme && 'text-center']"
      >
        {{ content.subtitle }}
      </p>
      <div class="grid grid-cols-1 gap-4" :class="[gridColumnsClass]">
        <div
          v-for="item in content.items"
          :key="item?.id"
          class="rounded-xl"
          :class="[
            twMerge(
              isLightTheme ? paddingClass(item) : 'p-4',
              item?.background === 'dark'
                ? 'dark bg-teal-900'
                : item?.background === 'white'
                  ? 'bg-white border border-gray-200'
                  : isGraySection
                    ? 'bg-white'
                    : 'light',
              layoutClass(item),
              gapClass(item),
              item?.rows === 2 && 'md:row-span-2',
              item?.rows === 3 && 'md:row-span-3',
              itemSpanClass(item),
              item?.alignment === 'center' && 'flex justify-center items-center'
            ),
          ]"
        >
          <div
            class="flex flex-col grow w-full"
            :class="contentLayoutClass(item)"
          >
            <span
              v-if="item?.caption"
              class="text-sm mb-1.5 block"
              :class="[twMerge(item?.background === 'dark' && 'text-stone-50')]"
              >{{ item?.caption }}</span
            >
            <h3
              class="m-0"
              :class="[
                titleSizeClass(item),
                isLightTheme ? 'font-semibold leading-7' : 'font-bold',
                item?.alignment === 'center' && 'text-center',
                item?.background === 'dark'
                  ? 'text-stone-50'
                  : 'text-stone-800',
              ]"
            >
              {{ item?.title }}
            </h3>
            <vue-markdown
              v-if="item?.content"
              class="prose text-sm md:text-body"
              :class="[
                isLightTheme
                  ? 'mt-2 leading-snug md:leading-6'
                  : 'mt-1.5 leading-6',
                // Only space off the CTA block when there is one to space off.
                item?.CTA?.length > 0 ? 'mb-5' : 'mb-0',
                item?.alignment === 'center' && 'text-center',
                item?.background === 'dark' && 'text-stone-50',
              ]"
              :source="item?.content || ''"
            ></vue-markdown>
            <div
              v-if="item?.CTA?.length > 0 && item?.imagePosition !== 'bottom'"
              :class="ctaContainerClass(item)"
            >
              <Button
                v-for="button in item?.CTA"
                :key="button?.label"
                :color="button?.color"
                :link="button?.url"
                :class="ctaButtonClass(item)"
                @click="handleCTA(button?.url)"
              >
                {{ button?.label }}
              </Button>
            </div>
          </div>
          <img
            v-if="isIcon(item)"
            class="size-11 shrink-0 block object-contain"
            :class="[item?.alignment === 'center' && 'self-center']"
            :src="iconUrl(item?.image)"
            :alt="item?.image?.alternativeText || ''"
          />
          <div
            v-else-if="hasImage(item)"
            class="shrink-0"
            :class="[IMAGE_WIDTH[item?.imagePosition]]"
          >
            <NuxtImg
              class="rounded-xl object-cover w-full h-full"
              :src="String(item?.image?.url)"
              :alt="String(item?.image?.alternativeText)"
            />
          </div>
          <div
            v-if="item?.CTA?.length > 0 && item?.imagePosition === 'bottom'"
            :class="ctaContainerClass(item)"
          >
            <Button
              v-for="button in item?.CTA"
              :key="button?.label"
              :color="button?.color"
              :link="button?.url"
              :class="ctaButtonClass(item)"
              @click="handleCTA(button?.url)"
            >
              {{ button?.label }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import VueMarkdown from 'vue-markdown-render'
import { twMerge } from 'tailwind-merge'
import { strapiImageGetters } from '~/utils/strapiImageGetters'

const { toggleDateRequestSidebar } = useUiState()
const route = useRoute()
const emit = defineEmits(['openContactForm', 'openCaseStudyForm'])
const { content = {} } = defineProps<{ content: any }>()

/**
 * Full-bleed background per `background` value. Vertical rhythm is owned by
 * `.section-y` (see `app/assets/css/sections.css`), so this maps the tint only;
 * the default paints nothing.
 */
const SECTION_BACKGROUND: Record<string, string> = {
  dark: 'bg-teal-900',
  gray: 'bg-stone-75',
}

/**
 * Card layout for each image position. An icon occupies the same slot as a
 * picture, so these positions move it too.
 */
const ITEM_LAYOUT: Record<string, string> = {
  right: 'flex flex-col md:flex-row gap-4',
  left: 'flex flex-col-reverse md:flex-row-reverse gap-4',
  top: 'flex flex-col-reverse gap-4',
  bottom: 'flex flex-col gap-4',
  background: 'image-position-background flex flex-col gap-4',
}

/** Title size, set per card in the CMS. Defaults to medium when unset. */
/**
 * Keyed by the CMS font-size value normalised to bare alphanumerics. Strapi's
 * GraphQL API sanitises enum values to valid GraphQL names — hyphens become
 * underscores (`extra-large` → `extra_large`) — so we normalise before lookup
 * instead of keying on the raw admin value, which never arrives verbatim.
 */
const TITLE_SIZE: Record<string, string> = {
  small: 'text-sm',
  medium: 'text-base',
  large: 'text-lg',
  extralarge: 'text-xl',
  xxl: 'text-2xl',
  xxxl: 'text-3xl',
  xxxxl: 'text-4xl',
}

/** CMS-driven inner padding for a card. Falls back to the default when unset. */
const PADDING: Record<string, string> = {
  small: 'p-2',
  medium: 'p-6',
  large: 'p-6 md:p-10',
}

/** How much of the card the image takes, per position. */
const IMAGE_WIDTH: Record<string, string> = {
  right: 'w-full md:w-1/2',
  left: 'w-full md:w-1/2',
  bottom: 'w-full',
  background: 'background-image',
}

/** Written out in full so Tailwind's scanner sees each class literally. */
const GRID_COLUMNS: Record<number, string> = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
  5: 'md:grid-cols-5',
  6: 'md:grid-cols-6',
}

const COL_SPAN: Record<number, string> = {
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  5: 'md:col-span-5',
  6: 'md:col-span-6',
}

const MAX_COLUMNS = 6

const sectionBackgroundClass = computed(
  () => SECTION_BACKGROUND[content?.background] ?? ''
)

/**
 * Every non-dark background (default and `gray`) uses the light treatment:
 * the redesigned type scale and white cards. Icon sizing is not part
 * of it — an SVG renders the same on every background.
 */
const isLightTheme = computed(() => content?.background !== 'dark')

/**
 * Cards go white only on the gray band. Their default `.light` fill is the same
 * stone-75 as that band, so they would otherwise disappear into it — on every
 * other background the original fill still reads fine.
 */
const isGraySection = computed(() => content?.background === 'gray')

/**
 * How many columns the row has from `md:` up, set per section in the CMS and
 * defaulting to the original three.
 */
const sectionColumns = computed(() => {
  const columns = Math.round(content?.columns)
  return columns >= 1 && columns <= MAX_COLUMNS ? columns : 3
})

const gridColumnsClass = computed(() => GRID_COLUMNS[sectionColumns.value])

/**
 * How many of the section's columns one card spans, from its own `columns`
 * field. Capped at the section's column count — a wider span would add an
 * implicit track and push the rest of the row out of alignment. One column
 * needs no class, which is also what an unset value renders as.
 */
const itemSpanClass = (item: any): string => {
  const span = Math.min(Math.round(item?.columns) || 1, sectionColumns.value)
  return COL_SPAN[span] ?? ''
}

/** The CMS title-size class for a card, defaulting to large when unset. */
/** Strip to bare alphanumerics so both the admin and GraphQL enum forms match. */
const normalizeEnumValue = (value: string): string =>
  (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')

const titleSizeClass = (item: any): string =>
  TITLE_SIZE[normalizeEnumValue(item?.fontSize)] ?? TITLE_SIZE.large

/** Inner padding from the CMS, defaulting to the original scale when unset. */
const paddingClass = (item: any): string =>
  PADDING[normalizeEnumValue(item?.padding)] ?? 'p-5 md:p-6'

/**
 * An SVG upload is icon artwork rather than a picture, so it renders small and
 * as-authored instead of filling the card. Returns its URL, or '' for anything
 * that is not an SVG served over http(s).
 */
const iconUrl = (image: any): string => {
  const isSvg =
    image?.mime === 'image/svg+xml' || /\.svg(?:[?#]|$)/i.test(image?.url ?? '')
  const url = isSvg ? strapiImageGetters.getImageUrl(image) : ''
  return /^https?:\/\//.test(url) ? url : ''
}

/** An SVG is an icon on every background — the size is not a theme choice. */
const isIcon = (item: any): boolean => !!iconUrl(item?.image)

/** Whether a full-size picture is rendered; icons are sized and placed apart. */
const hasImage = (item: any): boolean => !!item?.image?.url && !isIcon(item)

/**
 * The card arranges its text around the image, and an icon sits in that same
 * slot so `imagePosition` moves it too. With no position set an icon defaults
 * to `top`, since it would otherwise fall after the text in document order.
 */
const layoutClass = (item: any): string =>
  ITEM_LAYOUT[item?.imagePosition] ?? (isIcon(item) ? ITEM_LAYOUT.top : '')

/**
 * How the text/CTA panel sits next to the image.
 * - `spread` (original): the panel stretches to the image's height and the CTA
 *   is pushed to the far end.
 * - `centered`: the panel hugs its content, keeps the CTA grouped with the text,
 *   and is vertically centered against the image.
 * Defaults to `centered` while we trial the new layout — flip the default once
 * it's confirmed.
 */
const isCenteredContent = (item: any): boolean =>
  normalizeEnumValue(item?.contentLayout || 'centered') !== 'spread'

/**
 * Whether the CTA buttons span the panel width. `full` (default) keeps the
 * original full-width buttons; `auto` sizes them to their label with compact
 * padding instead.
 */
const isFullWidthCta = (item: any): boolean =>
  normalizeEnumValue(item?.ctaWidth || 'full') !== 'auto'

/** Panel positioning next to the image; only centering differs from default. */
const contentLayoutClass = (item: any): string =>
  isCenteredContent(item) ? 'md:self-center' : ''

/**
 * Gap between the image and the text/CTA panel. Widens to match the roomier
 * `large` inner padding; every other padding keeps the default `gap-4` from
 * `ITEM_LAYOUT`.
 */
const gapClass = (item: any): string =>
  normalizeEnumValue(item?.padding) === 'large' ? 'gap-12' : ''

/**
 * The CTA row. In `spread` mode it grows so the buttons can be pushed to the
 * far end; in `centered` mode it hugs the buttons right below the text.
 */
const ctaContainerClass = (item: any): string =>
  isCenteredContent(item) ? 'flex gap-2' : 'flex gap-2 grow'

/** Per-button sizing: full width vs. compact auto width, plus end-alignment
 * for the spread layout. */
const ctaButtonClass = (item: any): string =>
  twMerge(
    isFullWidthCta(item) ? 'w-full' : 'px-4.5! py-2.5',
    !isCenteredContent(item) && 'self-end'
  )

const handleCTA = (action: string) => {
  if (action === 'openEventRequestForm') {
    toggleDateRequestSidebar(route.name?.toString(), 'strapi_component')
  } else if (action === 'openPartnerContactForm') {
    emit('openContactForm')
  } else if (action === 'openCaseStudyForm') {
    emit('openCaseStudyForm')
  }
}
</script>
<style lang="css" scoped>
:deep(.prose) strong {
  color: inherit;
}

.grid-title {
  letter-spacing: 0.014em;
}

.light {
  background: rgba(249, 247, 245, 1);
}

.image-position-background {
  position: relative;
  background: none;
  z-index: 1;
  overflow: hidden;
}

.image-position-background::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  z-index: -1;
  background: rgba(255, 255, 255, 0.7);
}

.image-position-background.dark::before {
  background: rgba(0, 0, 0, 0.7);
}

.background-image {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  z-index: -2;
  object-fit: cover;
  width: 100%;
  height: 100%;
}
</style>
